import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { TextInput } from '@guildeducationinc/recess';
import DeleteOutlineIcon from '@guildeducationinc/recess/icons/DeleteOutlineIcon';
import { GradingScaleType, } from '@src/types';
import { validateGradeInRange, validatePercentage } from '@src/utilities/gradeValidation';
import { classNames, MuiMultiSelect, TrackableButton, useLocale } from 'pe-core-frontend';
import { useEffect, useState } from 'react';
export const GradePayouts = ({ gradeOptions, gradingScaleType, isInvertedScale = false, gradePayouts, programLevelOptions, onChange, readonly, }) => {
    const { t } = useLocale();
    // Local state to track unsaved changes
    const [localPayouts, setLocalPayouts] = useState(gradePayouts || []);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [minGradeErrors, setMinGradeErrors] = useState({});
    const [maxGradeErrors, setMaxGradeErrors] = useState({});
    // Sync with parent when gradePayouts prop changes
    useEffect(() => {
        setLocalPayouts(gradePayouts || []);
        setHasUnsavedChanges(false);
    }, [gradePayouts]);
    const isRange = (g) => 'minValue' in g && 'maxValue' in g;
    const range = isRange(gradeOptions) ? gradeOptions : undefined;
    const gradeOptionsValueLabel = Array.isArray(gradeOptions)
        ? gradeOptions.map(g => ({ value: g, label: String(g) }))
        : isRange(gradeOptions)
            ? [
                {
                    value: gradeOptions.minValue,
                    label: `${gradeOptions.minValue}`,
                },
                {
                    value: gradeOptions.maxValue,
                    label: `${gradeOptions.maxValue}`,
                },
            ]
            : 'items' in gradeOptions
                ? (gradeOptions.items || []).map(it => ({
                    value: it.value,
                    label: it.label,
                }))
                : Object.values(gradeOptions).map(g => ({
                    value: g,
                    label: t(`GradeType.${g}`),
                }));
    const handleGradePayoutChange = (index, propName, value) => {
        const newGradePayouts = [...localPayouts];
        newGradePayouts[index] = {
            ...newGradePayouts[index],
            [propName]: value,
        };
        setLocalPayouts(newGradePayouts);
        setHasUnsavedChanges(true);
    };
    const handleMinGradeChange = (index, event) => {
        const raw = event.target.value;
        const valueNum = raw === '' ? undefined : Number(raw);
        // Use shared validation utility
        const rangeValidation = validateGradeInRange(valueNum, range, isInvertedScale, t);
        if (!rangeValidation.isValid) {
            setMinGradeErrors({ ...minGradeErrors, [index]: rangeValidation.errorMessage });
            return;
        }
        const percentValidation = validatePercentage(valueNum, t);
        if (!percentValidation.isValid) {
            setMinGradeErrors({ ...maxGradeErrors, [index]: percentValidation.errorMessage });
            return;
        }
        setMinGradeErrors({ ...minGradeErrors, [index]: undefined });
        handleGradePayoutChange(index, 'gtPercentage', raw === '' || isNaN(Number(raw)) ? undefined : Number(raw));
    };
    const handleMaxGradeChange = (index, event) => {
        const raw = event.target.value;
        const valueNum = raw === '' ? undefined : Number(raw);
        // Use shared validation utilities
        const rangeValidation = validateGradeInRange(valueNum, range, isInvertedScale, t);
        if (!rangeValidation.isValid) {
            setMaxGradeErrors({ ...maxGradeErrors, [index]: rangeValidation.errorMessage });
            return;
        }
        const percentValidation = validatePercentage(valueNum, t);
        if (!percentValidation.isValid) {
            setMaxGradeErrors({ ...maxGradeErrors, [index]: percentValidation.errorMessage });
            return;
        }
        setMaxGradeErrors({ ...maxGradeErrors, [index]: undefined });
        handleGradePayoutChange(index, 'ltePercentage', raw === '' || isNaN(Number(raw)) ? undefined : Number(raw));
    };
    const handleAddGradePayout = () => {
        const newGradePayout = {
            payout: undefined,
            programLevels: [],
            gradeTypes: [],
            ltePercentage: undefined,
            gtPercentage: undefined,
        };
        setLocalPayouts([...localPayouts, newGradePayout]);
        setHasUnsavedChanges(true);
    };
    const handleDeleteGradePayout = (index) => {
        const newGradePayouts = localPayouts.filter((_, i) => i !== index);
        // Clean up errors for deleted row
        const newMinErrors = { ...minGradeErrors };
        const newMaxErrors = { ...maxGradeErrors };
        delete newMinErrors[index];
        delete newMaxErrors[index];
        // Re-index errors for rows after the deleted one
        Object.keys(newMinErrors).forEach(key => {
            const keyNum = parseInt(key);
            if (keyNum > index) {
                newMinErrors[keyNum - 1] = newMinErrors[keyNum];
                delete newMinErrors[keyNum];
            }
        });
        Object.keys(newMaxErrors).forEach(key => {
            const keyNum = parseInt(key);
            if (keyNum > index) {
                newMaxErrors[keyNum - 1] = newMaxErrors[keyNum];
                delete newMaxErrors[keyNum];
            }
        });
        setMinGradeErrors(newMinErrors);
        setMaxGradeErrors(newMaxErrors);
        setLocalPayouts(newGradePayouts);
        setHasUnsavedChanges(true);
    };
    const handleSave = () => {
        // Warn if any rows are missing payout percentage
        const missing = localPayouts.some(p => p.payout === undefined || p.payout === null);
        if (missing) {
            // Keep UX minimal: rely on Unsaved changes indicator
        }
        const validPayouts = localPayouts.filter(payout => payout.payout !== undefined && payout.payout !== null);
        onChange(validPayouts);
        setHasUnsavedChanges(false);
    };
    const handleCancel = () => {
        // Revert to original payouts
        setLocalPayouts(gradePayouts || []);
        setHasUnsavedChanges(false);
        setMinGradeErrors({});
        setMaxGradeErrors({});
    };
    return (_jsxs("div", { className: 'my-3', children: [_jsx("div", { className: 'flex justify-between items-center mb-2', children: !readonly && hasUnsavedChanges && (_jsxs("div", { className: 'flex gap-2', children: [_jsx("span", { className: 'text-sm text-orange-600 self-center', children: t('Unsaved changes') }), _jsx(TrackableButton, { btnType: 'mui', enText: 'Save Grade Payouts', tag: 'save_grade_payouts', variant: 'contained', color: 'primary', size: 'small', onClick: handleSave, children: t('Save') }), _jsx(TrackableButton, { btnType: 'mui', enText: 'Cancel Grade Payouts', tag: 'cancel_grade_payouts', variant: 'outlined', size: 'small', onClick: handleCancel, children: t('Cancel') })] })) }), _jsxs("table", { className: 'min-w-full divide-y divide-gray-900 border border-1 border-gray-900', children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { className: classNames('p-2 text-left', 'text-md'), children: t('Payout Percentage') }), _jsx("th", { className: classNames('p-2 text-left', 'text-md'), children: t('Program Levels') }), gradingScaleType !== GradingScaleType.RANGE && (_jsx("th", { className: classNames('p-2 text-left', 'text-md'), children: t('Grade Types') })), gradingScaleType === undefined && (_jsx("th", { className: classNames('p-2 text-left', 'text-md'), children: t('Percentage Range') })), gradingScaleType === GradingScaleType.RANGE && (_jsx("th", { className: classNames('p-2 text-left', 'text-md'), children: t('Grade Range') })), !readonly && _jsx("th", { className: 'p-2 text-md text-left', children: t('Actions') })] }) }), _jsxs("tbody", { className: 'divide-y divide-gray-900', children: [localPayouts.map((item, index) => (_jsxs("tr", { children: [_jsxs("td", { className: 'p-2', children: [!readonly && (_jsx(TextInput, { label: '', append: gradingScaleType === GradingScaleType.RANGE ? '' : '%', value: item.payout ?? '', onChange: event => handleGradePayoutChange(index, 'payout', event.target.value === '' || isNaN(Number(event.target.value))
                                                    ? undefined
                                                    : Number(event.target.value)) })), readonly && `${item.payout ?? ''}${item.payout !== undefined ? '%' : ''}`] }), _jsx("td", { className: 'p-2', children: _jsx(MuiMultiSelect, { disabled: readonly, label: '', onChange: event => handleGradePayoutChange(index, 'programLevels', event.selection?.map(x => x.value) ?? []), options: programLevelOptions, value: item.programLevels ?? [] }) }), gradingScaleType !== GradingScaleType.RANGE && (_jsx("td", { className: 'p-2', children: (() => {
                                            const baseMap = new Map(gradeOptionsValueLabel.map(o => [String(o.value), o]));
                                            (item.gradeTypes ?? []).forEach(v => {
                                                const key = String(v);
                                                if (!baseMap.has(key)) {
                                                    baseMap.set(key, { value: v, label: key });
                                                }
                                            });
                                            const mergedOptions = Array.from(baseMap.values());
                                            return (_jsx(MuiMultiSelect, { disabled: readonly, label: '', onChange: event => handleGradePayoutChange(index, 'gradeTypes', event.selection?.map(x => x.label) ?? []), options: mergedOptions, value: item.gradeTypes ?? [] }));
                                        })() })), (gradingScaleType === GradingScaleType.RANGE || gradingScaleType === undefined) && (_jsx("td", { className: 'p-2', children: _jsxs("div", { className: 'flex gap-2', children: [!readonly && (_jsxs(_Fragment, { children: [_jsx(TextInput, { label: '', append: gradingScaleType === GradingScaleType.RANGE ? '' : '%', prepend: '>', type: 'number', errors: minGradeErrors[index], value: item.gtPercentage ?? '', onChange: event => handleMinGradeChange(index, event) }), _jsx(TextInput, { label: '', append: gradingScaleType === GradingScaleType.RANGE ? '' : '%', prepend: '<=', type: 'number', errors: maxGradeErrors[index], value: item.ltePercentage ?? '', onChange: event => handleMaxGradeChange(index, event) })] })), readonly && (_jsxs(_Fragment, { children: [item.gtPercentage !== undefined && `> ${item.gtPercentage}`, item.ltePercentage !== undefined && `<= ${item.ltePercentage}`, item.ltePercentage !== undefined && item.gtPercentage !== undefined && ' '] }))] }) })), !readonly && (_jsx("td", { className: 'p-2', children: _jsx(TrackableButton, { btnType: 'icon', enText: 'Delete', tag: `grade_payout_${index}`, label: t('Delete'), appearance: 'minimal', icon: _jsx(DeleteOutlineIcon, { size: 20 }), onClick: () => handleDeleteGradePayout(index) }) }))] }, `payout-${index}`))), !readonly && (_jsx("tr", { children: _jsx("td", { colSpan: gradingScaleType !== GradingScaleType.RANGE ? 5 : 4, className: 'p-2', children: _jsx(TrackableButton, { btnType: 'mui', enText: 'Add Grade Payout Row', tag: 'add_grade_payout_row', variant: 'contained', color: 'primary', size: 'small', onClick: handleAddGradePayout, children: t('+ Add Grade Payout Row') }) }) }))] })] })] }));
};
